import React, { useEffect, useState } from 'react';
import { Table, Spin, Button, Tooltip, Empty } from 'antd';
import { NumericFormat } from 'react-number-format';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import styles from './index.module.scss';
import {
	PreviewSvg,
	QuickbooksEmployee,
	SortSvgBottom,
	SortSvgTop,
} from 'utils/svgs';
import { EJournalStatus, ExpenseJournalStatus } from 'constants/Data';
import { DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
	deleteExpenseJournalAction,
	getExpenseJournalPreviewEntriesAction,
} from 'redux/action/expenseJournalAction';
import { previewOneOffExpenseJournalEntriesAction } from 'redux/action/oneOffAction';
import { AppDispatch } from 'redux/store';
import ExpenseGroupPreviewJournal from '../PreviewJournal';
import ExpenseJournalDeleteModal from '../ExpenseJournalDeleteModal';
import { toastText } from 'utils/utils';

interface RowData {
	id: string;
	date: string;
	dateRange: string; // replacing pay period
	qboJournalNo: string;
	qboLink?: string;
	amount: number;
	publishedBy: string;
	notes: string;
	actions?: string; // for actions column
	expenseGroupId?: string; // Add expense group ID
	mode?: string; // Journal mode: "temp" or other values
}

interface ExpenseJournalEntriesTableProps {
	data: RowData[];
	total?: number;
	currentPage?: number;
	onPageChange?: (page: number) => void;
	onTableChange?: (pagination: any, filters: any, sorter: any) => void;
	loading?: boolean;
	hasMore?: boolean;
	isLoadingMore?: boolean;
	onLoadMore?: () => void;
	expenseGroupOptions?: { value: string; label: string }[];
	year?: number;
	status?: 'All' | ExpenseJournalStatus;
	search?: string;
}

const ExpenseJournalEntriesTable: React.FC<ExpenseJournalEntriesTableProps> = ({
	data,
	onTableChange,
	loading = false,
	hasMore = false,
	isLoadingMore = false,
	onLoadMore,
	expenseGroupOptions = [],
	year,
	status,
	search,
}) => {
	const { Column } = Table as any;
	const dispatch = useDispatch<AppDispatch>();
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	const hasActiveFilters = Boolean(
		search?.trim() ||
		year ||
		(status && status !== 'All')
	);
	const [selectedJournalData, setSelectedJournalData] = useState<any>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [journalToDelete, setJournalToDelete] = useState<any>(null);
	const [loadingJournalId, setLoadingJournalId] = useState<string | null>(null);

	// Redux state for delete operation and preview loading
	const { deleteLoading, previewEntriesLoading } = useSelector(
		(state: any) => state.expenseJournal
	);

	// Get company setting for summarized view
	const companySummarizedView = useSelector(
		(state: any) =>
			state.companies?.currentCompany?.isSummarizedJournalView ?? false
	);

	const qboMode = process.env.REACT_APP_QBO_MODE
		? process.env.REACT_APP_QBO_MODE
		: 'sandbox';

	// Infinite scroll logic
	useEffect(() => {
		const handleScroll = (event: any) => {
			if (!hasMore || isLoadingMore || loading) return;

			const { scrollTop, scrollHeight, clientHeight } = event.target;
			const threshold = 10; // Load more when 10px from bottom

			if (scrollTop + clientHeight >= scrollHeight - threshold) {
				onLoadMore?.();
			}
		};

		// Attach scroll listener to the actual table body element
		const tableBody = document.querySelector('.ant-table-body');
		if (tableBody) {
			tableBody.addEventListener('scroll', handleScroll);
			return () => {
				tableBody.removeEventListener('scroll', handleScroll);
			};
		}
	}, [hasMore, isLoadingMore, loading, onLoadMore]);

	// Handle preview journal
	const handlePreviewJournal = async (journalData: any) => {
		try {
			setSelectedJournalData(journalData);
			setLoadingJournalId(journalData.id);

			// Check if this is a one-off journal
			const isOneOffJournal = journalData.mode === 'oneOff';

			if (isOneOffJournal) {
				// Use one-off preview route for one-off journals
				const params = {
					companyId: localStorage.getItem('companyId'),
					journalId: journalData.id,
					isSummarizedJournalView: companySummarizedView,
				};

				await dispatch(previewOneOffExpenseJournalEntriesAction(params));
			} else {
				// Use normal preview route for regular journals
				// Extract date range from journal data
				const dateRange = journalData.dateRange;
				const [startDate, endDate] = dateRange.split(' - ');

				// Call the preview API with the journal data including journalId for unique identification
				const params = {
					expenseGroupId: journalData.expenseGroupId,
					fromDate: startDate,
					toDate: endDate,
					journalId: journalData.id, // Add journalId for unique identification
					isSummarizedJournalView: companySummarizedView,
				};

				await dispatch(getExpenseJournalPreviewEntriesAction(params));
			}

			setIsPreviewOpen(true);
			setLoadingJournalId(null);
		} catch (error) {
			console.error('Error fetching journal preview:', error);
			setLoadingJournalId(null);
		}
	};

	// Handle close preview
	const handleClosePreview = () => {
		setIsPreviewOpen(false);
		setSelectedJournalData(null);
		setLoadingJournalId(null);
	};

	// Handle delete journal
	const handleDeleteJournal = (journalData: any) => {
		setJournalToDelete(journalData);
		setIsDeleteModalOpen(true);
	};

	// Handle confirm delete
	const handleConfirmDelete = async () => {
		if (!journalToDelete) return;

		try {
			await dispatch(deleteExpenseJournalAction(journalToDelete.id));
			toastText('Expense journal deleted successfully', 'success');
			setIsDeleteModalOpen(false);
			setJournalToDelete(null);
		} catch (error) {
			console.error('Error deleting journal:', error);
		}
	};

	// Handle cancel delete
	const handleCancelDelete = () => {
		setIsDeleteModalOpen(false);
		setJournalToDelete(null);
	};

	const CustomEmptyState = () => (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description={
				loading
					? null
					: hasActiveFilters
						? "No Journal Entry records match the selected filter criteria"
						: "No Journal Entry records are currently available"
			}
			style={{
				padding: '40px 20px',
				margin: '20px 0'
			}}
		/>
	);

	return (
		<div className={styles['journal-entries-container__table']}>
			<Table
				dataSource={data}
				pagination={false}
				className={`${styles['dynamic-table']} journal-entries-table`}
				onChange={onTableChange}
				loading={loading}
				scroll={{ y: '47vh' }}
				rowKey={(r: RowData) => r.id}
				locale={{
					emptyText: <CustomEmptyState />
				}}
			>
				<Column
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Date
						</p>
					)}
					dataIndex="date"
					key="date"
					className="bg-white"
					width="100px"
					defaultSortOrder="ascend"
					sorter={() => {
						return null as any;
					}}
					sortDirections={['ascend', 'descend', 'ascend']}
					sortIcon={(data: any) => {
						return data.sortOrder === 'ascend' ? (
							<SortSvgTop />
						) : (
							<SortSvgBottom />
						);
					}}
					render={(value: string) => {
						return (
							<p className={styles['dynamic-table__date']}>
								{moment.utc(value).format('MM/DD/YYYY')}
							</p>
						);
					}}
				/>
				<Column
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Date Range
						</p>
					)}
					dataIndex="dateRange"
					key="dateRange"
					width="150px"
					className="bg-white"
					render={(value: string) => {
						// Format date range from YYYY-MM-DD - YYYY-MM-DD to MM/DD/YYYY - MM/DD/YYYY
						if (value && value.includes(' - ')) {
							const [startDate, endDate] = value.split(' - ');
							const formattedStartDate = moment(startDate).format('MM/DD/YYYY');
							const formattedEndDate = moment(endDate).format('MM/DD/YYYY');
							return (
								<p className={styles['dynamic-table__pay-period']}>
									{`${formattedStartDate} - ${formattedEndDate}`}
								</p>
							);
						}
						return (
							<p className={styles['dynamic-table__pay-period']}>{value}</p>
						);
					}}
					defaultSortOrder="ascend"
					align="left"
				/>
				<Column
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							QuickBooks Journals No.#
						</p>
					)}
					dataIndex="qboLink"
					key="qboLink"
					className="bg-white"
					width="120px"
					render={(value: any, data: any) => {
					const baseUrl =
					qboMode === 'prod'
					? 'https://app.qbo.intuit.com/app'
					: 'https://app.sandbox.qbo.intuit.com/app';
						return data?.status === 'Published' ? (
							<a
								className={`${styles['dynamic-table__journalNo']} ${data?.status === EJournalStatus.DRAFT && 'pointer-event-none'
									}  `}
								href={`${baseUrl}/journal?txnId=${data?.qboLink}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<span>#{data?.qboJournalNo}</span>
								<QuickbooksEmployee />
							</a>
						) : (
							<p
								className={`${styles['dynamic-table__journalNo']
									} ${'pointer-event-none'}  `}
							>
								{data.status === 'Deleted' ? (
									<span>#{data?.qboJournalNo}</span>
								) : (
									<span>{ }</span>
								)}
							</p>
						);
					}}
				/>
				<Column
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Amount
						</p>
					)}
					dataIndex="amount"
					key="amount"
					className="bg-white"
					render={(value: number) => {
						return (
							<div style={{ textAlign: 'right' }}>
								<NumericFormat
									value={value}
									thousandSeparator=","
									decimalScale={2}
									fixedDecimalScale={true}
									displayType="text"
									prefix="$"
									renderText={(value) => (
										<p className={styles['dynamic-table__amount']}>
											{value ?? 0}
										</p>
									)}
								/>
							</div>
						);
					}}
					width="100px"
				/>
				<Column
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Published By
						</p>
					)}
					dataIndex="publishedBy"
					key="publishedBy"
					className="bg-white"
					width="120px"
					render={(value: string) => {
						return (
							<p className={styles['dynamic-table__published-by']}>{value}</p>
						);
					}}
				/>
				<Column
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Notes
						</p>
					)}
					dataIndex="notes"
					key="notes"
					className="bg-white"
					width="280px"
					render={(value: string) => {
						const truncatedText =
							value.length > 50 ? value.substring(0, 50) + '...' : value;
						const shouldShowTooltip = value.length > 50;

						return (
							<span
								className={styles['dynamic-table__notes']}
								style={{
									fontSize: '12px',
									display: '-webkit-box',
									WebkitLineClamp: 4,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
									wordWrap: 'break-word',
									wordBreak: 'break-word',
									lineHeight: '1.4',
								}}
							>
								{shouldShowTooltip ? (
									<Tooltip
										title={value}
										placement="bottom"
										overlayClassName="filter-tooltip"
									>
										{truncatedText}
									</Tooltip>
								) : (
									value
								)}
							</span>
						);
					}}
				/>
				<Column
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Status
						</p>
					)}
					dataIndex="status"
					key="status"
					width="70px"
					className="bg-white dynamic-table-status"
					render={(value: any) => {
						return (
							<div className={styles['dynamic-table__journal-status']}>
								<span
									className={
										value === 'Published'
											? styles['dynamic-table__journal-status-published']
											: styles['dynamic-table__journal-status-draft']
									}
								>
									{value}
								</span>
							</div>
						);
					}}
					defaultSortOrder="ascend"
					sorter={() => {
						return null as any;
					}}
					sortDirections={['ascend', 'descend', 'ascend']}
					sortIcon={(data: any) => {
						return data.sortOrder === 'ascend' ? (
							<SortSvgTop />
						) : (
							<SortSvgBottom />
						);
					}}
				/>
				<Column
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Actions
						</p>
					)}
					dataIndex="actions"
					key="actions"
					className="bg-white"
					width="80px"
					render={(_: any, record: any) => {
						// Hide preview button if mode is "temp", otherwise show and enable it
						const isTempMode = record.mode === 'temp';
						const isPreviewLoading =
							previewEntriesLoading && loadingJournalId === record.id;

						return (
							<div className={styles['dynamic-table__journal-actions']}>
								<span
									className={styles['dynamic-table__journal-actions-preview']}
									onClick={
										isTempMode || isPreviewLoading
											? undefined
											: () => handlePreviewJournal(record)
									}
									style={{
										visibility: isTempMode ? 'hidden' : 'visible',
										pointerEvents:
											isTempMode || isPreviewLoading ? 'none' : 'auto',
									}}
								>
									{isPreviewLoading ? (
										<img
											src="/assets/gifs/loading-black.gif"
											alt="loading"
											height={20}
											width={20}
										/>
									) : (
										<PreviewSvg />
									)}
								</span>
								<span
									className={styles['dynamic-table__journal-actions-delete']}
								>
									<Button
										type="link"
										icon={<DeleteOutlined style={{ fontSize: '16px' }} />}
										style={{ color: '#e74031' }}
										onClick={() => handleDeleteJournal(record)}
									/>
								</span>
							</div>
						);
					}}
				/>
			</Table>
			{isLoadingMore && (
				<div
					style={{
						textAlign: 'right',
						padding: '0 20px',
						fontSize: '12px',
						color: '#344735',
						marginTop: '8px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
					}}
				>
					<Spin size="small" className={styles['loading-more-spinner']} />
					<span style={{ marginLeft: '8px' }}>Loading more journals...</span>
				</div>
			)}
			{!hasMore && data.length > 0 && (
				<div
					style={{
						textAlign: 'right',
						padding: '0 20px',
						color: '#344735',
						fontSize: '12px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						marginTop: '8px',
					}}
				>
					No more journals
				</div>
			)}

			{/* Preview Journal Modal */}
			{selectedJournalData && (
				<ExpenseGroupPreviewJournal
					isOpen={isPreviewOpen}
					onClose={handleClosePreview}
					selectedExpenseGroup={
						selectedJournalData.mode === 'oneOff'
							? 'all' // One-off journals use 'all' as expense group
							: selectedJournalData.expenseGroupId || ''
					}
					dateRange={
						selectedJournalData.dateRange
							? [
								dayjs(selectedJournalData.dateRange.split(' - ')[0]),
								dayjs(selectedJournalData.dateRange.split(' - ')[1]),
							]
							: null
					}
					expenseGroupOptions={expenseGroupOptions}
					existingJournalData={selectedJournalData}
					isExistingJournal={true}
					journalId={selectedJournalData.id} // Pass journalId for unique identification
				/>
			)}

			{/* Delete Journal Modal */}
			<ExpenseJournalDeleteModal
				isOpen={isDeleteModalOpen}
				onCancel={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				isLoading={deleteLoading}
				journalStatus={journalToDelete?.status || 'Draft'}
				journalNumber={journalToDelete?.qboJournalNo}
			/>
		</div>
	);
};

export default ExpenseJournalEntriesTable;
